<div class="page-container">
  <!-- Page Header -->
  <app-page-header
    [title]="isUserLike() ? 'Stations' : 'Manage Stations'"
    [subtitle]="isUserLike() ? 'Select coverage cities to browse stations in those areas' : 'Manage radio stations and their properties'"
    [showBackButton]="true"
    backRoute="/userhome" backLabel="Back to Dashboard">
    <button *ngIf="canCreateStation()" class="btn-primary" routerLink="/managestations/add">
      <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
      </svg>
      Add New Station
    </button>
  </app-page-header>

  <!-- User-friendly Stations Cards (for role 'user') -->
  <div class="content-wrapper user-view" *ngIf="isUserLike(); else adminStationsBlock">
    <section class="user-coverage-filter" aria-label="Filter stations by coverage city">
      <div class="user-coverage-filter-header">
        <mat-icon aria-hidden="true">location_city</mat-icon>
        <div>
          <h2 class="user-coverage-filter-title">Find stations by coverage city</h2>
          <p class="user-coverage-filter-desc">
            Select one or more cities to see stations that cover those areas.
          </p>
        </div>
      </div>

      <app-coverage-city-select
        [multiple]="true"
        [stationCoverageOnly]="true"
        label="Coverage Cities"
        placeholder="Search and select cities"
        hint="Only cities with active station coverage are listed"
        [(ngModel)]="selectedCoverageCities"
        (citiesChange)="onUserCoverageCitiesChange($event)">
      </app-coverage-city-select>
    </section>

    <div *ngIf="!userHasSelectedCities" class="user-browse-prompt" role="status">
      <mat-icon aria-hidden="true">travel_explore</mat-icon>
      <div>
        <p class="user-browse-prompt-title">Start by choosing a coverage city</p>
        <p class="user-browse-prompt-text">
          Select one or more cities above to browse stations in those areas.
        </p>
      </div>
    </div>

    <ng-container *ngIf="userHasSelectedCities">
      <div *ngIf="loading" class="inline-loading" role="status">
        <mat-spinner diameter="22"></mat-spinner>
        <span>Loading stations for {{ selectedCitiesLabel }}…</span>
      </div>

      <div *ngIf="!loading && userStationsLoadError" class="inline-error" role="alert">
        <mat-icon aria-hidden="true">error_outline</mat-icon>
        <span>{{ userStationsLoadError }}</span>
      </div>

      <div *ngIf="!loading && !userStationsLoadError && stations.length === 0" class="inline-empty" role="status">
        <mat-icon aria-hidden="true">radio</mat-icon>
        <div>
          <p class="inline-empty-title">No stations found</p>
          <p class="inline-empty-text">
            There are no stations for {{ selectedCitiesLabel }}. Try another city or add more cities to your search.
          </p>
        </div>
      </div>

      <div *ngIf="!loading && !userStationsLoadError && stations.length > 0" class="user-stations-results">
        <p class="user-results-summary">
          {{ totalItems }} {{ totalItems === 1 ? 'station' : 'stations' }} in {{ selectedCitiesLabel }}
        </p>

        <div class="stations-grid">
          <div *ngFor="let station of stations; trackBy: trackByStationId" class="station-card-user">
            <div class="card-header">
              <img *ngIf="station.logo_url; else placeholderLogo" class="logo" [src]="station.logo_url" [alt]="station.name + ' logo'" />
              <ng-template #placeholderLogo>
                <div class="logo placeholder" aria-hidden="true">{{ station.call_sign || station.name?.charAt(0) }}</div>
              </ng-template>
              <div class="title-block">
                <h3 class="name">{{ station.name }}</h3>
                <div class="meta">
                  <span class="call-sign">{{ station.call_sign }}</span>
                  <span class="dial">{{ station.dial_position }} FM</span>
                  <span class="format">{{ station.format }}</span>
                </div>
              </div>
            </div>

            <div class="card-body">
              <div class="row">
                <span class="label">Coverage city</span>
                <span class="value">{{ formatCoverageCity(station) }}</span>
              </div>
              <div class="row">
                <span class="label">Audience</span>
                <span class="value">{{ station.audience || '-' }}</span>
              </div>
              <div class="row actions">
                <a *ngIf="station.url" class="link" [href]="station.url" target="_blank" rel="noopener" [attr.aria-label]="'Visit ' + station.name + ' website'">
                  <mat-icon>open_in_new</mat-icon> Website
                </a>
                <button class="btn-outline" type="button"
                  [disabled]="!station.coverage_map_url"
                  (click)="viewCoverageMap(station)"
                  [attr.aria-label]="'View coverage map for ' + station.name">
                  <mat-icon>map</mat-icon>
                  View coverage map
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="pagination-panel" *ngIf="totalItems > 0">
          <p class="pagination-summary">Showing {{ paginationStart }}–{{ paginationEnd }} of {{ totalItems }} stations</p>
        </div>
        <div *ngIf="totalPages > 1" class="pagination" role="navigation" aria-label="Stations pagination">
          <button type="button" class="pagination-btn" [class.disabled]="currentPage === 1" (click)="goToPage(currentPage - 1)"
            [disabled]="currentPage === 1">
            Previous
          </button>
          <div class="pagination-numbers">
            <button type="button" *ngFor="let page of (getPaginationPages() || []); trackBy: trackByPage" class="pagination-number"
              [class.active]="page === currentPage" (click)="goToPage(page)">
              {{ page }}
            </button>
          </div>
          <button type="button" class="pagination-btn" [class.disabled]="currentPage === totalPages" (click)="goToPage(currentPage + 1)"
            [disabled]="currentPage === totalPages">
            Next
          </button>
        </div>
      </div>
    </ng-container>

    <!-- In-app coverage map preview -->
    <div *ngIf="showCoveragePreview" class="coverage-overlay" (click)="closeCoveragePreview()" aria-modal="true" role="dialog">
      <div class="coverage-modal" (click)="$event.stopPropagation()">
        <div class="coverage-modal-header">
          <h3 class="modal-title">{{ selectedCoverageStation?.name }} — Coverage Map</h3>
          <button class="modal-close" (click)="closeCoveragePreview()" aria-label="Close">
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <div class="coverage-modal-body">
          <img *ngIf="coveragePreviewUrl; else noMap" [src]="coveragePreviewUrl" [alt]="selectedCoverageStation?.name + ' coverage map'" />
          <ng-template #noMap>
            <div class="no-map">No coverage map available.</div>
          </ng-template>
        </div>
      </div>
    </div>
  </div>

  <!-- Admin/Manager Table Block -->
  <ng-template #adminStationsBlock>
  <div class="content-wrapper">
    <div class="admin-filters-row" aria-label="Station filters" *ngIf="permissionService.getBaseRole() === 'admin'">
      <mat-form-field appearance="outline" class="filter-field" subscriptSizing="dynamic">
        <mat-label>Filter by Owner</mat-label>
        <mat-select
          [(ngModel)]="selectedOwnerId"
          (selectionChange)="onOwnerFilterChange()"
          [disabled]="loadingOwners"
          panelClass="owner-station-filter-panel">
          <mat-option [value]="''">All Owners</mat-option>
          <mat-option *ngFor="let owner of owners" [value]="owner.id">{{ owner.name }}</mat-option>
        </mat-select>
      </mat-form-field>
    </div>

    <app-common-table
      [data]="stations"
      [columns]="tableColumns"
      [actions]="tableActions"
      [loading]="loading"
      [sortBy]="sortBy"
      [sortOrder]="sortOrder"
      (sortChange)="onSortChange($event)"
      emptyMessage="No stations found" emptyDescription="Get started by adding your first radio station."
      [showAddButton]="canCreateStation()" addButtonText="Add New Station" (addClick)="navigateToAdd()">

      <!-- Custom table rows -->
      <ng-template #rowTemplate let-station let-i="index">
        <tr class="table-row">
          <td class="td-id" *ngIf="isAdmin">{{ station.id }}</td>
          <td class="td-call-sign">
            <span class="call-sign">{{ station.call_sign }}</span>
          </td>
          <td class="td-name">
            <div class="station-name-cell">
              <div class="station-logo-thumb" *ngIf="hasStationLogo(station); else stationLogoPlaceholder">
                <img
                  [src]="station.logo_url"
                  [alt]="station.name + ' logo'"
                  class="station-logo-thumb__img"
                  loading="lazy"
                  (error)="onStationLogoError(station)">
              </div>
              <ng-template #stationLogoPlaceholder>
                <div class="station-logo-thumb station-logo-thumb--placeholder" aria-hidden="true">
                  <span>{{ stationInitial(station) }}</span>
                </div>
              </ng-template>

              <div class="station-info">
                <div class="station-name">{{ station.name }}</div>
                <a *ngIf="station.url" [href]="station.url" target="_blank" rel="noopener noreferrer" class="url-link">
                  Visit website
                </a>
              </div>
            </div>
          </td>
          <td class="td-visibility" *ngIf="isAdminOrOwner">
            <div class="visibility-cell">
              <label class="rad-toggle" [attr.title]="station.is_active === 1 ? 'Visible to advertisers' : 'Hidden from advertisers'">
                <input
                  type="checkbox"
                  [checked]="station.is_active === 1"
                  [disabled]="togglingVisibilityId === station.id"
                  (change)="toggleStationVisibility(station, $event)"
                  [attr.aria-label]="(station.is_active === 1 ? 'Hide' : 'Show') + ' ' + station.name">
                <span class="rad-toggle-slider"></span>
              </label>
              <span class="visibility-text" [class.visibility-text--off]="station.is_active === 0">
                {{ station.is_active === 1 ? 'On' : 'Off' }}
              </span>
            </div>
          </td>
          <td class="td-role">
            <span [class]="roleBadgeClass(station)">{{ roleLabel(station) }}</span>
          </td>
          <td class="td-format">
            <span class="format-badge">{{ station.format }}</span>
          </td>
          <td class="td-dial">{{ station.dial_position }}</td>
          <td class="td-coverage">
            <div class="coverage-cities" *ngIf="station.coverage_cities?.length; else coverageAreaFallback">
              <span class="coverage-city-first">{{ firstCoverageCity(station) }}</span>
              <span
                *ngIf="station.coverage_cities.length > 1"
                class="coverage-more"
                [attr.data-tooltip]="getCitiesTooltip(station)"
                [attr.title]="getCitiesTooltip(station)">
                +{{ station.coverage_cities.length - 1 }} more
              </span>
            </div>
            <ng-template #coverageAreaFallback>
              <span *ngIf="station.coverage_area; else noCoverage" class="coverage-area">{{ formatCoverageCity(station) }}</span>
              <ng-template #noCoverage>
                <span class="no-data">—</span>
              </ng-template>
            </ng-template>
          </td>
          <td class="td-audience">
            <span *ngIf="station.audience; else noAudience" class="audience">{{ station.audience }}</span>
            <ng-template #noAudience>
              <span class="no-data">—</span>
            </ng-template>
          </td>
          <td class="td-actions">
            <div class="action-buttons" *ngIf="canEditStation(station) || canDeleteStation(station); else readOnlyStation">
              <button *ngIf="canEditStation(station)" type="button" class="btn-edit" (click)="navigateToEdit(station)"
                title="Edit station" aria-label="Edit station">
                <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z">
                  </path>
                </svg>
              </button>
              <button *ngIf="canDeleteStation(station)" type="button" class="btn-delete" (click)="deleteStation(station)"
                title="Delete station" aria-label="Delete station">
                <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16">
                  </path>
                </svg>
              </button>
            </div>
            <ng-template #readOnlyStation>
              <span class="no-data">View only</span>
            </ng-template>
          </td>
        </tr>
      </ng-template>
    </app-common-table>

    <div class="pagination-panel" *ngIf="!loading && totalItems > 0">
      <p class="pagination-summary">Showing {{ paginationStart }}–{{ paginationEnd }} of {{ totalItems }} stations</p>
    </div>
    <div *ngIf="totalPages > 1" class="pagination" role="navigation" aria-label="Stations pagination">
      <button type="button" class="pagination-btn" [class.disabled]="currentPage === 1" (click)="goToPage(currentPage - 1)"
        [disabled]="currentPage === 1">
        Previous
      </button>

      <div class="pagination-numbers">
        <button type="button" *ngFor="let page of (getPaginationPages() || []); trackBy: trackByPage" class="pagination-number"
          [class.active]="page === currentPage" (click)="goToPage(page)">
          {{ page }}
        </button>
      </div>

      <button type="button" class="pagination-btn" [class.disabled]="currentPage === totalPages" (click)="goToPage(currentPage + 1)"
        [disabled]="currentPage === totalPages">
        Next
      </button>
    </div>
  </div>
  </ng-template>
</div>
